// Tiny sound-effect engine. Sounds are SYNTHESISED with the WebAudio API — there are
// no audio assets to download, so this costs nothing in build size or load time.
//
// Design rules (deliberately unobtrusive):
//  - every cue is short (<= ~300ms) and quiet (low master gain);
//  - there is NO background music / ambient loop — those are what get annoying;
//  - nothing ever plays on a timer, only as direct feedback to a player action;
//  - muting is one click and is remembered across sessions;
//  - every call is try/catch-guarded, so audio can never break gameplay.
//
// The AudioContext is created lazily on the first cue (browsers only allow audio after
// a user gesture, and our cues always follow a click).
import { useSyncExternalStore } from 'react';

const MUTE_KEY = 'sfx_muted';
const MASTER_GAIN = 0.14; // intentionally low — feedback, not a soundtrack

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Ctx = any;

let ctx: Ctx = null;
let master: Ctx = null;
let muted = localStorage.getItem(MUTE_KEY) === '1';
// Silenced while an ad is on screen (services/adBreak.ts). Deliberately NOT `muted`: it is
// never persisted, never shown on the navbar toggle, and ends by itself — the player's own
// preference must come out of an ad exactly as it went in.
let adSuspended = false;

const listeners = new Set<() => void>();

export function isMuted(): boolean { return muted; }

export function setMuted(v: boolean) {
  muted = v;
  try { localStorage.setItem(MUTE_KEY, v ? '1' : '0'); } catch { /* private mode */ }
  applyGain();
  listeners.forEach(l => l());
}

/** Silence every cue while an ad is on screen. Not the player's mute — see adBreak.ts. */
export function setAdSuspended(v: boolean) {
  adSuspended = v;
  applyGain();
}

// Zeroing the master gain as well as refusing new cues also cuts the tail of a cue that
// was already sounding when the ad began.
function applyGain() {
  if (master) { try { master.gain.value = (muted || adSuspended) ? 0 : MASTER_GAIN; } catch { /* ignore */ } }
}

export function toggleMuted() { setMuted(!muted); }

/** React hook: true when sound is muted (re-renders the mute button on change). */
export function useMuted(): boolean {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => muted,
    () => muted,
  );
}

function audio(): Ctx {
  if (ctx) return ctx;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AC = (window as any).AudioContext ?? (window as any).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = (muted || adSuspended) ? 0 : MASTER_GAIN;
    master.connect(ctx.destination);
  } catch {
    ctx = null;
  }
  return ctx;
}

/**
 * One short enveloped tone. `type` picks the timbre, `from`/`to` sweep the pitch,
 * `dur` is seconds, `delay` offsets the start (used to build small arpeggios).
 */
function tone(from: number, to: number, dur: number, type: OscillatorType = 'sine', delay = 0, peak = 1) {
  const c = audio();
  if (!c || muted || adSuspended) return;
  try {
    const t0 = c.currentTime + delay;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(from, t0);
    if (to !== from) osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), t0 + dur);
    // Quick attack, smooth decay — avoids clicks/pops at the edges.
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + Math.min(0.02, dur * 0.3));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g); g.connect(master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  } catch { /* audio is best-effort */ }
}

// Resume a context suspended by the browser's autoplay policy. Safe to call often.
function wake() {
  const c = audio();
  try { if (c && c.state === 'suspended') c.resume(); } catch { /* ignore */ }
}

export const sfx = {
  /** Generic UI confirmation (buy, run, toggle). */
  click:    () => { wake(); tone(720, 720, 0.05, 'square', 0, 0.5); },
  /** Scan / probe a remote host. */
  scan:     () => { wake(); tone(380, 900, 0.18, 'sine', 0, 0.7); },
  /** Connected to a remote node. */
  connect:  () => { wake(); tone(520, 780, 0.12, 'triangle', 0, 0.8); tone(780, 780, 0.08, 'sine', 0.12, 0.6); },
  /** Attack launched. */
  attack:   () => { wake(); tone(200, 120, 0.22, 'sawtooth', 0, 0.5); },
  /** Hack / mission succeeded — small rising arpeggio. */
  success:  () => { wake(); tone(523, 523, 0.09, 'sine', 0, 0.9); tone(659, 659, 0.09, 'sine', 0.08, 0.9); tone(784, 784, 0.16, 'sine', 0.16, 0.9); },
  /** Hack failed / action rejected — short descending buzz. */
  fail:     () => { wake(); tone(300, 150, 0.26, 'triangle', 0, 0.7); },
  /** Credits banked (infection payout, reward). */
  credits:  () => { wake(); tone(700, 700, 0.05, 'sine', 0, 0.8); tone(900, 900, 0.05, 'sine', 0.06, 0.8); tone(1150, 1150, 0.1, 'sine', 0.12, 0.8); },
  /** A download/upload finished. */
  transfer: () => { wake(); tone(660, 990, 0.14, 'sine', 0, 0.7); },
  /** Something was deleted/stopped. */
  remove:   () => { wake(); tone(420, 260, 0.1, 'square', 0, 0.45); },
};
